import type { SportType } from '@/@types/graphql-types';
import type { MaybeRefOrGetter } from '@vueuse/core';
import type { UpcomingEvent } from '@/features/sports/types/interfaces';

import { shiftDateWithTimezone } from '@/helpers/date-helper';
import { useQuery } from '@/helpers/composables/useApollo';

import {
	GetMultiStepSportOverviewDocument,
	GetMultiStepSportOverviewQuery,
	GetMultiStepSportOverviewQueryVariables,
} from '@/features/sports/graphql/queries/GetMultiStepSportOverview.query';
import { MultiStepSportEvent, Platform, SportEventAggregation, SportEventStatus } from '@/@types/graphql-types';

import { toValue } from '@vueuse/core';
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';

interface UpcomingEventsDataOptions {
	sportType: MaybeRefOrGetter<SportType>;
	filterBy: MaybeRefOrGetter<'sport' | 'competition'>;
	filterByParam: MaybeRefOrGetter<Array<SportType | string>>;
}

type TimeGroup = 'live' | 'today' | 'upcoming';

export function useUpcomingEventsData(options: UpcomingEventsDataOptions) {
	const route = useRoute();
	const languageStore = storeToRefs(useLanguageStore());

	const queryVariables = computed(() => {
		const localeTodayStart = shiftDateWithTimezone(languageStore.localeObject.value.timezone);
		const filterBy = toValue(options.filterBy);
		const filterByParam = toValue(options.filterByParam);

		return {
			platform: Platform.Web,
			language: languageStore.language.value,
			country: languageStore.country.value,
			first: ['sport', 'competition'].includes(filterBy) ? 1 : 10,
			bucketSize: filterBy === 'sport' ? 10 : 10, // @todo check if we want to keep it for multi sport queries
			groupBy: SportEventAggregation.Sport,
			filter: {
				startAt: {
					from: localeTodayStart.toISOString(),
					to: null,
				},
				competitionIds: filterBy === 'competition' ? [filterByParam].flat() : [],
				sports: filterBy === 'sport' ? [filterByParam as SportType[]].flat() : [],
			},
			timezoneOffset: 0,
		};
	});

	const enabled = computed(() => {
		return !toValue(options.filterByParam) || !(route.meta!.data as string)?.includes('multistep');
	});

	const upcomingEventsQuery = useQuery<GetMultiStepSportOverviewQuery, GetMultiStepSportOverviewQueryVariables>(
		GetMultiStepSportOverviewDocument,
		queryVariables,
		{ enabled }
	);

	const upcomingEvents = computed(() => {
		const events = upcomingEventsQuery.result.value?.sportEventBucketsV2.edges ?? [];

		return events
			.map(edge => edge.node.edges)
			.flat()
			.filter(edge => edge?.node != null)
			.map(edge => edge!.node as MultiStepSportEvent);
	});

	const events = computed<Record<TimeGroup, UpcomingEvent[]>>(() => {
		const allEvents = upcomingEvents.value.map(event => {
			const lineItems = getLineItems(event);
			const competitionStageText = getCompetitionStageText(event);
			const localStartAt = changeTimezone(
				languageStore.localeObject.value.timezone,
				new Date(event.content.startAt ?? '')
			); // needs to be put in current webLcoale's timezone
			const startAt = new Date(event.content.startAt ?? ''); // needs to be put in current webLcoale's timezone

			return {
				id: event.id,
				lineItems,
				fullPath: event.parent?.content.fullPath,
				startAt: startAt,
				localStartAt: localStartAt,
				sport: event.sport,
				competition: {
					text: event.competition?.content?.name,
					fullPath: event.competition?.content?.fullPath,
				},
				competitionStageText,
				offers: event?.offers,
				watchNowOffer: event.watchNowOffer,
				mysportlistEntry: event.mysportlistEntry,
				sportEvent: event, // used for snowplow context and tracking
			} as UpcomingEvent;
		});

		const localeToday = changeTimezone(languageStore.localeObject.value.timezone);
		const liveMatches = allEvents.filter(event => event?.sportEvent?.content?.status === SportEventStatus.Live);
		const todayMatches = allEvents.filter(event => isToday(localeToday, event.localStartAt));
		const upcomingMatches = allEvents.filter(event => !isToday(localeToday, event.localStartAt));

		return {
			live: liveMatches,
			today: todayMatches,
			upcoming: upcomingMatches,
		};
	});

	const eventTimeGroup = computed<'live' | 'today' | 'upcoming'>(() => {
		if (events.value.live.length > 0) {
			return 'live';
		} else if (events.value.today.length > 0) {
			return 'today';
		} else {
			return 'upcoming';
		}
	});

	const isDataLoading = computed(() => {
		return upcomingEventsQuery.loading.value || !toValue(options.filterByParam);
	});

	/**
	 * Depending on sport, it can the team's name or the course of the event.
	 * That's why it's called line items.
	 */
	function getLineItems(event: MultiStepSportEvent): UpcomingEvent['lineItems'] {
		// MultiStepSportEvent
		return [{ text: event.parent?.content.name ?? '' }];
	}

	function getCompetitionStageText(event: MultiStepSportEvent) {
		const { name, number } = event.content?.competitionStage || {};

		// capitalise racing (e.g. Practice, Qualifying, ...)
		const formulaRacingName = `${name?.slice(0, 1).toUpperCase()}${name?.slice(1).toLowerCase()}`;
		return [formulaRacingName, number].filter(item => item !== null).join(' ');
	}

	return {
		events,
		eventTimeGroup,
		isDataLoading,
	};
}

// depending on where the JS machine is, this can be different.
// so we force it by comparing it to the timezone of the current webLocale.
function changeTimezone(timeZone: string, targetDate = new Date()) {
	// we can hard code en-US because in the end we're only interested in the Date object
	const localisedDateString = targetDate.toLocaleDateString('en-US', { timeZone, timeZoneName: 'short' });
	return new Date(localisedDateString);
}

function isToday(today: Date, targetDate: Date) {
	return today.toDateString() === targetDate.toDateString();
}
